import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Box, Divider, Grid, Typography } from '@mui/material';
import useFileClient from 'context/Files/FileClient';
import { TransactionStatus } from 'enums';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTransactionPaymentValidation } from 'utils/validation/payments/transaction-payment-submittion';
import { usePaymentMethod } from '..';
import { useSubmitPayment } from '../../hooks/data/useSubmitPayment';
import { useTokenDecode } from '../../hooks/data/useTokenDecode';
import PaymentFiles from './files';
import PaymentForm from './form';

import { transaction } from 'apis';
import { useFileContext } from 'context/FilesV2';
import { isNil } from 'lodash';
import { formatFiles } from 'utils';

const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const PaymentMethodBank = ({ transactionDetailsQuery, filesQuery, token }) => {
    const validationSchema = useTransactionPaymentValidation();
    const resolver = useYupValidationResolver(validationSchema);
    const methods = useForm({
        resolver,
        mode: 'onSubmit',
        reValidateMode: 'onBlur'
    });
    const { transactionId } = useTokenDecode();
    const { submitPayment } = useSubmitPayment();
    const client = useFileClient();
    const reCaptchaRef = useRef();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { selectedPaymentMethod } = usePaymentMethod();
    const fgRefs = useRef([]);
    const { fileGroups: fileGroupsV2, control: filesControl } = useFileContext();
    // This hook watches for changes in the fileClient
    // and uploads the files to the server
    // useHandleFileUpload();

    // This hook watches if at least one file has been
    // selected and enables/disables the submit button
    // const ready = useReadyForSubmit();

    const transactionStatus = transactionDetailsQuery.data?.StatusId;
    const [uploadData, setUploadData] = useState(null);

    const uploadFiles = async () => {
        const formData = new FormData();
        let index = 0;
        fileGroupsV2.forEach((group) => {
            group.bundles?.forEach((bundle) => {
                bundle.files?.forEach((file) => {
                    if (file) {
                        if (file.dbId) formData.append(`Files[${index}].Id`, file.dbId);
                        formData.append(`Files[${index}].BundleId`, bundle.id);
                        formData.append(`Files[${index}].RecordGuid`, transactionId);
                        formData.append(`Files[${index}].ProjectTable`, 4);
                        index++;
                    }
                });
            });
        });

        const options = isNil(token)
            ? ''
            : {
                  headers: {
                      Authorization: `Bearer ${token}`
                  }
              };

        const fileIds = await transaction.uploadFiles(formData, options);
    };

    useEffect(() => {
        const bindFiles = async () => {
            const captcha = await reCaptchaRef.current.executeAsync();
            uploadFiles();
            await submitPayment({
                transactionId,
                paymentMethod: selectedPaymentMethod,
                captcha,
                token,
                paidAmount: transactionDetailsQuery.data?.Total,
                ...uploadData
            });
            setUploadData(null);
        };
        if (uploadData) {
            bindFiles();
        }
    }, [uploadData, uploadFiles]);

    const onSubmit = async (data) => {
        try {
            const validationPromises = [];
            fgRefs?.current?.forEach((fg) => {
                const bundleValidationPromise = fg?.validateBundles();
                if (bundleValidationPromise) validationPromises.push(bundleValidationPromise);
            });

            // check if all promises return true
            const validationResults = await Promise.all(validationPromises);
            if (validationResults.some((result) => result.some((r) => r === false))) {
                enqueueSnackbar(t('FileRequiredFieldMissing'), {
                    variant: 'error'
                });
                return;
            }
            await filesControl.uploadFileGroups();
            setUploadData(data);
        } catch (error) {
            enqueueSnackbar(error?.message, {
                variant: 'error'
            });
        }
    };

    // Initialize files
    useEffect(() => {
        try {
            const formattedFiles = formatFiles(filesQuery?.data);
            if (formattedFiles !== undefined) {
                filesControl.initialize(formattedFiles);
            } else {
                filesControl.initialize([]);
            }
        } catch (error) {
            console.error('error', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesQuery?.data]);

    const readOnly = transactionStatus !== TransactionStatus.pendingPayment && transactionStatus !== TransactionStatus.rechecked;
    return (
        <FormProvider {...methods}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Divider />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h4" fontSize="16px">
                        {t('SelectedPaymentMethod')}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <PaymentForm readOnly={readOnly} data={transactionDetailsQuery.data} reCaptchaRef={reCaptchaRef} />
                </Grid>
                <Grid item xs={12}>
                    <Alert severity="warning" sx={{ cursor: 'pointer', mb: 2 }}>
                        <AlertTitle>{t('Warning')}</AlertTitle>
                        {t('TransactionBeforeSubmitWarning')}
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <PaymentFiles
                        recordGuid={transactionId}
                        readOnly={readOnly}
                        fileGroups={filesQuery.data ?? []}
                        token={token}
                        fgRefs={fgRefs}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ReCAPTCHA sitekey={recaptchaKey} size="invisible" ref={reCaptchaRef} style={{ visibility: 'hidden' }} />
                </Grid>
                {(transactionStatus === TransactionStatus.pendingPayment || transactionStatus === TransactionStatus.rechecked) && (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="end">
                            <LoadingButton
                                loading={methods?.formState.isSubmitting}
                                disableElevation
                                disabled={
                                    readOnly ||
                                    fileGroupsV2?.every(
                                        (fg) =>
                                            fg.bundles === undefined ||
                                            fg.bundles.length === 0 ||
                                            fg.bundles.every((b) => b.files === undefined || b.files.length === 0)
                                    )
                                }
                                size="large"
                                variant="contained"
                                sx={{ alignSelf: 'end' }}
                                onClick={methods?.handleSubmit(onSubmit)}
                            >
                                {t('FinalSubmit')}
                            </LoadingButton>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </FormProvider>
    );
};

PaymentMethodBank.propTypes = {
    // react query object
    transactionDetailsQuery: PropTypes.object,
    // react query object
    filesQuery: PropTypes.object,
    // token from the url
    token: PropTypes.string
};

export default PaymentMethodBank;
